import { pdf } from '@react-pdf/renderer'
import type { OrderResponse } from '../contracts/orders'
import type { ExportFormat } from '../types/exports'

/**
 * Export service for generating PDF documents from order data
 */
export class ExportService {
  /**
   * Generate PDF blob from order data
   */
  static async generatePDF(order: OrderResponse): Promise<Blob> {
    try {
      // Import PDF component dynamically to avoid circular dependency
      const { createOrderPDFDocument } = await import('./pdf-generator')
      
      // Generate PDF blob
      const blob = await pdf(createOrderPDFDocument(order)).toBlob()
      
      return blob
    } catch (error) {
      console.error('PDF generation failed:', error)
      throw new Error('Failed to generate PDF document')
    }
  }

  /**
   * Download PDF file directly in browser
   */
  static async downloadPDF(order: OrderResponse): Promise<void> {
    try {
      const blob = await this.generatePDF(order)
      const fileName = `order-${order.id}-${Date.now()}.pdf`
      
      // Create download link and trigger download
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('PDF download failed:', error)
      throw new Error('Failed to download PDF')
    }
  }

  /**
   * Helper function is no longer needed - unit comes from contract
   */

  /**
   * Generate simple text content for order (temporary solution)
   */
  static generateSimplePDFContent(order: OrderResponse): string {
    const content = `
ZEIGLER INTERNAL - ORDER EXPORT
=====================================

Order Details:
- Order ID: #${order.id}
- Title: ${order.title}
- Status: ${order.status.charAt(0).toUpperCase() + order.status.slice(1)}
- Created: ${this.formatDate(order.created_at)}

${order.project ? `
Project Information:
- Project Name: ${order.project.name}
- Project Code: ${order.project.code}
` : ''}

${order.job_number ? `Job Number: ${order.job_number}` : ''}
${order.delivery_date ? `Delivery Date: ${this.formatDate(order.delivery_date)}` : ''}
${order.notes ? `Notes: ${order.notes}` : ''}

${order.items && order.items.length > 0 ? `
Order Items (${order.items.length} items):
${order.items.map(item => 
  `- ${item.product_name} - Quantity: ${item.quantity} ${item.unit}`
).join('\n')}
` : 'No items added yet.'}

Generated on: ${new Date().toLocaleString()}
=====================================
    `.trim()
    
    return content
  }

  /**
   * Get formatted filename for export
   */
  static getFileName(orderId: number, format: ExportFormat): string {
    const timestamp = Date.now()
    return `order-${orderId}-${timestamp}.${format}`
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
} 